import { useEffect, useMemo, useRef, useState } from 'react';

import path from 'path';

import { HelpOutline as HelpOutlineIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, IconButton, Link as MuiLink, Portal, Stack, Typography } from '@mui/material';
import {
	GridColDef,
	GridFilterModel,
	GridInitialState,
	GridPaginationMeta,
	GridPaginationModel,
	GridSlotProps,
	GridSlots,
	PropsFromSlot,
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, DateRangePicker, Tooltip } from '~components';
import { pagePathSegments } from '~constants';
import { SkcDevicesFilter } from '~features/devices';
import { BaseReference } from '~interfaces';

import AccessLog from '../../interfaces/accessLog';
import SkcAccessLogsService from '../../services/skcAccessLogsService';
import SkcUsersAndNfcTagsFilter from '../autocompletes/skcUsersAndNfcTagsFilter';
import AccessEventChip from '../chips/accessEventChip';
import AccessActionTypeIcon from '../icons/accessActionTypeIcon';

const toolbarIdString = 'topology-access-rules-datagrid';

const service = new SkcAccessLogsService();

const defaultStartDate = dayjs().subtract(7, 'day').startOf('day').toDate();

const AccessLogsDataGrid = () => {
	const { t } = useTranslation();
	const paginationMetaRef = useRef<GridPaginationMeta>(undefined);

	const columns = useMemo<GridColDef<AccessLog>[]>(
		() => [
			{ field: 'id', headerName: 'ID', width: 80, hideable: false },
			{
				field: 'timestamp',
				headerName: t('timestamp'),
				valueFormatter: (value: Date) => value.toLocaleString(),
				width: 150,
				type: 'dateTime',
			},
			{
				field: 'device',
				headerName: t('device'),
				renderCell: (params) =>
					params.value != null ?
						<Tooltip
							title={params.value.id}
							sx={{ display: 'flex', height: 1, alignItems: 'center' }}
						>
							{params.value.label}
						</Tooltip>
					:	'-',
				flex: 1,
			},
			{
				field: 'key',
				headerName: `${t('accessTag')} / ${t('ui.label.user')}`,
				valueFormatter: (value: BaseReference) => value?.label ?? '-',
				flex: 1,
			},
			{
				// DO NOTICE! If we remove the column definition the organisation filter we use on
				// the datagrid will be reset to null. In hindsight this could have been considered
				// bad design... But normally we have filtering set directly on the datagrid
				// But let's prepare if we hypophetically have an org
				field: 'organisation',
				headerName: t('organisation'),
				valueFormatter: (value?: BaseReference) => value?.label ?? '-',
			},
			{
				field: 'rule',
				headerName: t('rule'),
				renderCell: (params) =>
					params.value ?
						<MuiLink
							component={ReactRouterLink}
							to={`/${path.join(pagePathSegments.Access, pagePathSegments.Rules, params.value.id)}`}
							state={{ from: location.pathname }}
						>
							{params.value.label}
						</MuiLink>
					:	'-',
				flex: 1,
			},
			{
				field: 'accessType',
				headerName: t('views.organisationManagement.devices.unlockType'),
				width: 110,
				renderCell: (params) =>
					params.value != null ?
						<Tooltip title={params.value} sx={{ display: 'flex', height: 1, alignItems: 'center' }}>
							<AccessActionTypeIcon actionType={params.value} fontSize='small' />
						</Tooltip>
					:	'-',
			},
			{
				field: 'deviceVerified',
				type: 'boolean',
				renderHeader: () => (
					<Stack spacing={1} direction='row' sx={{ alignItems: 'center' }}>
						<Typography fontWeight={500}>{t('ui.verified')}</Typography>
						<Tooltip title={t('verifiedByDeviceHelpText')}>
							<HelpOutlineIcon sx={{ width: '1rem', height: '1rem' }} />
						</Tooltip>
					</Stack>
				),
				align: 'left',
				headerAlign: 'left',
				width: 130,
			},
			{
				field: 'eventType',
				headerName: t('ui.label.event'),
				renderCell: (params) => <AccessEventChip status={params.value} />,
				flex: 1,
			},
		],
		[t]
	);

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({});
	const [fetchParameters, setFetchParameters] = useState<object>({ after: defaultStartDate });
	const { data, isLoading, isValidating, error, mutate } = useSWR(
		// Wait for the pagesize to be set, otherwise we do a double fetch on mount
		fetchParameters.pageSize != null ? [service.basePath, fetchParameters] : null,
		([_, args]) => {
			return service.getAccessLogs(args);
		},
		{
			keepPreviousData: true,
		}
	);

	// With cursor pagination we would normally lose information about our
	// previous pages. Keep track of this state so we are able to go back
	const [rowCountState, setRowCountState] = useState(data?.total || 0);
	const mapPageToNextCursor = useRef<Record<number, string | undefined>>({});
	const paginationMeta = useMemo(() => {
		if (data?.hasMore != null && data?.hasMore !== paginationMetaRef.current?.hasNextPage) {
			paginationMetaRef.current = {
				hasNextPage: data?.hasMore || mapPageToNextCursor.current[paginationModel.page] != null,
			};
		}

		return paginationMetaRef.current;
	}, [data?.hasMore, paginationModel.page]);

	useEffect(() => {
		setFetchParameters((prev) => ({
			...prev,
			cursor: mapPageToNextCursor.current[paginationModel.page - 1],
			pageSize: paginationModel.pageSize,
		}));
	}, [paginationModel, mapPageToNextCursor]);

	useEffect(() => {
		if (paginationMeta?.hasNextPage !== false) {
			setRowCountState(data?.total || -1);
		}
	}, [paginationMeta?.hasNextPage, data?.total]);

	const handleStateRestore = (state: GridInitialState) => {
		const searchQuery = state.filter?.filterModel?.quickFilterValues?.[0];
		const organisation = state.filter?.filterModel?.items.find(
			(el) => el.field === 'organisation'
		)?.value;

		setFetchParameters((prev) => ({
			...prev,
			pageSize: state.pagination?.paginationModel?.pageSize,
			organisationId: organisation?.id,
			searchQuery: searchQuery,
		}));
	};

	const handlePaginationChange = (model: GridPaginationModel) => {
		mapPageToNextCursor.current[paginationModel.page] = data?.nextCursor;
		setPaginationModel(model);
		setFetchParameters((prev) => ({
			...prev,
			cursor: mapPageToNextCursor.current[model.page - 1],
			pageSize: model.pageSize,
		}));
	};

	const handleFilterModelChange = (model: GridFilterModel) => {
		const searchQuery = model.quickFilterValues?.[0];
		const organisation = model.items.find((el) => el.field === 'organisation')?.value;

		mapPageToNextCursor.current = {};
		setPaginationModel((prev) => ({ ...prev, page: 0 }));

		setFetchParameters((prev) => ({
			...prev,
			cursor: undefined,
			organisationId: organisation?.id,
			searchQuery: searchQuery,
		}));
	};

	const handleFiltersChange = (value: {
		start?: Date;
		end?: Date;
		deviceId?: string;
		keyId?: string;
	}) => {
		mapPageToNextCursor.current = {};
		setPaginationModel((prev) => ({ ...prev, page: 0 }));

		const newFetchParameters = {
			after: value.start,
			before: value.end,
			deviceId: value.deviceId,
			keyId: value.keyId,
		};

		setFetchParameters((prev) => ({
			...prev,
			...newFetchParameters,
			cursor: undefined,
		}));
	};

	return (
		<Stack
			spacing={2}
			sx={{
				display: 'flex',
				height: 1,
			}}
		>
			<Box id={toolbarIdString} />
			<DataGrid
				disableColumnSorting
				snapshotKey='accl'
				columns={columns}
				error={error}
				loading={isLoading || isValidating}
				rows={data?.results ?? []}
				rowCount={rowCountState}
				onRowCountChange={(newRowCount) => setRowCountState(newRowCount)}
				paginationModel={paginationModel}
				onPaginationModelChange={handlePaginationChange}
				onFilterModelChange={handleFilterModelChange}
				onStateRestore={handleStateRestore}
				paginationMeta={paginationMeta}
				// Do notice that we disable this because of the cursor base pagination.
				// For cursor pagination we always have to start refresh
				disablePageUrlSync
				initialState={{
					columns: {
						columnVisibilityModel: {
							organisation: false,
						},
					},
				}}
				slots={{
					toolbar: Toolbar as GridSlots['toolbar'],
				}}
				slotProps={{
					toolbar: {
						organisationId: fetchParameters.organisationId,
						filters: {
							deviceId: fetchParameters.deviceId,
							keyId: fetchParameters.keyId,
							start: fetchParameters.after,
							end: fetchParameters.before,
						},
						onRefresh: mutate,
						onFiltersChange: handleFiltersChange,
					} as GridSlotProps['toolbar'],
					noRowsOverlay: {
						title: t('noResourceFound', { resource: t('logs') }),
					},
					noResultsOverlay: {
						title: t('noResourceFound', { resource: t('logs') }),
					},
				}}
				sx={{ flexGrow: 1, flexBasis: 200 }}
			/>
		</Stack>
	);
};

interface AccessLogFilter {
	deviceId: string;
	keyId: string;
	start: Date;
	end: Date;
}

interface ToolbarProps extends PropsFromSlot<GridSlots['toolbar']> {
	organisationId?: string;
	filters: Partial<AccessLogFilter>;
	onRefresh?: () => void;
	onFiltersChange?: (value: Partial<AccessLogFilter>) => void;
}

const Toolbar = (props: ToolbarProps) => {
	const { t } = useTranslation();

	return (
		<Portal container={() => document.getElementById(toolbarIdString)}>
			<DataGridToolbar
				organisationSyncMode='url'
				enableClearFilters={
					props.filters != null &&
					Object.values(props.filters).filter((el) => el != undefined).length !== 0
				}
				onClearFilters={() => props.onFiltersChange?.({ start: defaultStartDate })}
				filters={[
					<Box sx={{ width: '200px' }}>
						<SkcDevicesFilter
							organisationId={props.organisationId}
							size='small'
							value={props.filters?.deviceId}
							onChange={(newValue) =>
								props.onFiltersChange?.({ ...props.filters, deviceId: newValue?.id })
							}
						/>
					</Box>,
					<Box sx={{ width: '200px' }}>
						<SkcUsersAndNfcTagsFilter
							label={`${t('accessTag')} / ${t('ui.label.user')}`}
							organisationId={props.organisationId}
							size='small'
							value={props.filters?.keyId}
							onChange={(newValue) =>
								props.onFiltersChange?.({ ...props.filters, keyId: newValue?.skcId })
							}
						/>
					</Box>,
					<DateRangePicker
						value={{ start: props.filters?.start, end: props.filters?.end }}
						maxDate={dayjs().endOf('day').toDate()}
						onDatesChange={(newValue) => props.onFiltersChange?.({ ...props.filters, ...newValue })}
					/>,
				]}
			>
				<IconButton onClick={props.onRefresh}>
					<RefreshIcon />
				</IconButton>
			</DataGridToolbar>
		</Portal>
	);
};

export default AccessLogsDataGrid;
